import errorTypes from '../../constants/errorTypes';
import { urlUtils } from '../../utils/url';
import { fetchLabels } from '../../services/content';
import { fetchFlightTime } from '../../services/flight';
import { getProductsIncluded } from '../../utils/mappers/productsIncluded';
import { fetchPriceMatrix, addPackageToCart } from '../../services/package';
import {
  addHotelOnlyToCart,
  fetchHotelOnlyPriceMatrix,
} from '../../services/hotelOnly';
import { fetchPriceMatrix as fetchCombinationPriceMatrix } from '../../services/combination';

import * as ACTIONS from '../actionTypes/priceMatrixActionTypes';
import * as ExponeaUtils from '../../utils/logUtils/ExponeaUtils';
import * as PriceMatrixLogUtils from '../../utils/logUtils/PriceMatrixLogUtils';

const log = (data) => {
  const message = data?.translatedMessage;
  const loggingData = data?.result?.loggingData;

  // Log search
  if (loggingData) {
    PriceMatrixLogUtils.priceMatrixSearch(loggingData, message);
    ExponeaUtils.logSearch(loggingData, 'Geographical/HotelInfo');
  }

  // Log special offer
  const roomGroupList = data?.result?.roomGroups;
  if (roomGroupList) {
    for (let i = 0; i < roomGroupList.length; i += 1) {
      if (roomGroupList[i].specialOffer) {
        PriceMatrixLogUtils.showPriceMatrixSpecialOffer();
        break;
      }
    }
  }

  // Log rooms on request
  const hasRoomsOnRequest = data?.result?.hotelInformation?.hasRoomsOnRequest;
  if (hasRoomsOnRequest) {
    PriceMatrixLogUtils.showPriceMatrixOnRequestLink();
  }
};

export const toggleErrorPopup = (error) => {
  return {
    type: ACTIONS.TOGGLE_ERROR_POPUP,
    ...error,
  };
};

const initSuccess = (response, labels) => {
  return (dispatch) => {
    const isCombination = urlUtils.isCombination();

    if (!response || response.statusCode !== 200) {
      dispatch({
        type: ACTIONS.INIT_FAIL,
        message: response.translatedMessage,
      });
      return;
    }

    const { result } = response;
    const sessionHasExpired = result?.serviceCallInformation?.sessionHasExpired;

    if (sessionHasExpired) {
      dispatch(
        toggleErrorPopup({
          visible: true,
          errorType: errorTypes.SESSION_EXPIRED,
          buttonUrl: isCombination ? result?.summaryNoSessionLink : null,
        }),
      );
    }

    log(response);

    const updateUrl = result != null && result.searchInformation != null;

    if (updateUrl && !sessionHasExpired) {
      urlUtils.updateUrlWithPriceMatrixSearchResultParams(result);
    }

    const { general, alternativeHotelsLabels, ...rest } = labels;

    dispatch({
      type: ACTIONS.LABELS_INIT_SUCCESS,
      labels: {
        ...rest,
        ...general,
        productsIncludedParsed: getProductsIncluded(result?.packageTag, labels),
      },
      alternativeHotelsLabels,
    });

    if (!result.roomGroups && response.translatedMessage) {
      dispatch({
        type: ACTIONS.INIT_FAIL,
        message: response.translatedMessage,
      });
      return;
    }

    dispatch({
      type: ACTIONS.INIT_SUCCESS,
      alternativeHotels: result.alternativeHotels,
      hotelData: result.hotelInformation,
      message: response.translatedMessage,
      roomGroups: result.roomGroups,
      searchInformation: result.searchInformation,
      summaryReturnLink: result.summaryReturnLink,
      transportData: result.transport,
    });
  };
};

const getLabels = async (siteSettings) => {
  const promises = [
    fetchLabels(siteSettings, 'iwPriceMatrix'),
    fetchLabels(siteSettings, 'iwHotelList'),
  ];

  const [labels, alternativeHotelsLabels] = await Promise.all(promises);
  const { general, ...rest } = labels;

  return {
    ...rest,
    ...general,
    alternativeHotelsLabels,
  };
};

const getPriceMatrix = async (siteSettings, userId) => {
  const isCombination = urlUtils.isCombination();
  const isHotelOnly = urlUtils.getTypeFromUrl() === 'IndependentHotelOnly';

  let response;
  switch (true) {
    case isCombination:
      response = await fetchCombinationPriceMatrix(siteSettings, userId);
      break;
    case isHotelOnly:
      response = await fetchHotelOnlyPriceMatrix(siteSettings, userId);
      break;
    default:
      response = await fetchPriceMatrix(siteSettings, userId);
      break;
  }

  return response;
};

export const addToCart = () => {
  return async (dispatch, getState) => {
    const { common, hotelData, transportData } = getState();
    const { siteSettings, userId } = common;
    const { selectedRoomGroup } = hotelData;
    const { outBoundFlights, homeBoundFlights } = transportData ?? {};
    const { bookingEngineRoomGroupKey: accommodationKey, link } =
      selectedRoomGroup;
    const isHotelOnly = urlUtils.getTypeFromUrl() === 'IndependentHotelOnly';

    const sessionId = urlUtils.getSessionIdFromUrl();

    let model = {
      SiteId: siteSettings.siteId,
      SessionId: `${sessionId}`,
      UserId: userId,
      AccomodationKey: accommodationKey,
    };
    if (!isHotelOnly) {
      model = {
        ...model,
        homeboundTransportKey: homeBoundFlights[0].bookingEngineKey,
        outboundTransportKey: outBoundFlights[0].bookingEngineKey,
      };
    }

    dispatch({
      type: ACTIONS.ADD_TO_CART_INIT,
      loading: true,
    });

    const addToCartResponse = isHotelOnly
      ? await addHotelOnlyToCart(model, siteSettings)
      : await addPackageToCart(model, siteSettings);
    const { result } = addToCartResponse;

    if (!result.success) {
      dispatch({
        type: ACTIONS.ADD_TO_CART_FAIL,
        loading: false,
      });

      if (result.sessionHasExpired) {
        dispatch(
          toggleErrorPopup({
            errorType: errorTypes.SESSION_EXPIRED,
            visible: true,
          }),
        );
        return;
      }

      if (addToCartResponse.translatedMessage) {
        dispatch(
          toggleErrorPopup({
            errorType: errorTypes.SYSTEM_ERROR_TRANSLATED,
            visible: true,
            title: addToCartResponse.translatedMessage.title,
            message: addToCartResponse.translatedMessage.message,
          }),
        );
        return;
      }

      if (addToCartResponse.errorMessage) {
        dispatch(
          toggleErrorPopup({
            errorType: errorTypes.SYSTEM_ERROR,
            visible: true,
            message: addToCartResponse.errorMessage,
          }),
        );
      }

      return;
    }

    dispatch({
      type: ACTIONS.ADD_TO_CART_SUCCESS,
      loading: false,
    });

    window.location = `/${link}`;
  };
};

export const selectRoomGroup = (roomGroup) => {
  return {
    type: ACTIONS.SELECT_ROOM_GROUP,
    roomGroup,
  };
};

export const init = () => {
  return async (dispatch, getState) => {
    const { siteSettings, userId } = getState().common;

    dispatch({
      type: ACTIONS.INIT,
    });

    const [labels, priceMatrixResponse] = await Promise.all([
      getLabels(siteSettings),
      getPriceMatrix(siteSettings, userId),
    ]);

    dispatch(initSuccess(priceMatrixResponse, labels));
  };
};

export const getFlightTime = () => {
  return async (dispatch, getState) => {
    const { common, transportData } = getState();
    const { homeBoundFlights, outBoundFlights } = transportData;
    const { siteSettings } = common;

    const sessionId = urlUtils.getSessionIdFromUrl();

    const response = await fetchFlightTime(
      sessionId,
      homeBoundFlights[0].bookingEngineKey,
      outBoundFlights[0].bookingEngineKey,
      siteSettings,
    );

    dispatch({
      type: ACTIONS.GET_PRICE_MATRIX_FLIGHT_TIME_SUCCESS,
      flightTimeInformation: response?.result,
    });
  };
};
